// portal/src/components/WorkspaceContext.tsx
// ────────────────────────────────────────────────────────────────────
// Shares the "what is the user looking at" focus between the active
// screen (Outlet) and the ChatDock. A screen calls setFocus on selection;
// the dock reads focus and sends it as the agent target. Screens that set
// no focus leave it null -> the agent returns altitude guidance.
// ────────────────────────────────────────────────────────────────────
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { Focus } from "@/lib/agentApi";

interface WorkspaceValue {
  focus: Focus | null;
  setFocus: (f: Focus | null) => void;
}

const WorkspaceCtx = createContext<WorkspaceValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }): JSX.Element {
  const [focus, setFocus] = useState<Focus | null>(null);
  const value = useMemo(() => ({ focus, setFocus }), [focus]);
  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}

/** Read + set the current workspace focus. Safe outside a provider
 *  (returns a null focus + no-op setter) so screens never crash. */
export function useWorkspaceFocus(): WorkspaceValue {
  return useContext(WorkspaceCtx) ?? { focus: null, setFocus: () => {} };
}
